import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { HelpCircle, ChevronDown, Search, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/input';

const FAQ_SECTIONS = [
  {
    title: 'Démarrage & compte',
    items: [
      {
        q: 'Comment démarrer sur TRADEX AI ?',
        a: 'Créez votre compte gratuit en 30 secondes. Vous bénéficiez immédiatement de quelques analyses IA gratuites pour tester. Uploadez un graphique de trading (screenshot depuis TradingView, votre broker, etc.) et l\'IA vous propose Entry, Stop Loss et Take Profits en quelques secondes.',
      },
      {
        q: 'Ai-je besoin de compétences en trading ?',
        a: 'Non. TRADEX AI est conçu pour tous les niveaux. L\'IA vous explique le raisonnement derrière chaque analyse (ICT, structure de marché, gestion du risque). Pour aller plus loin, l\'onglet "Learn" contient des cours complets sur l\'analyse technique et le Money Management.',
      },
      {
        q: 'Puis-je utiliser TRADEX AI sur mobile ?',
        a: 'Oui. TRADEX AI est une PWA (Progressive Web App). Ajoutez-la à votre écran d\'accueil depuis Safari (iPhone) ou Chrome (Android) — vous obtenez une expérience quasi-native avec notifications push, mode plein écran et fonctionnement hors-ligne partiel.',
      },
      {
        q: 'Comment changer mon mot de passe ?',
        a: 'Rendez-vous dans "Mes paramètres" → onglet "Sécurité". Vous pouvez également utiliser "Mot de passe oublié" depuis la page de connexion si vous ne vous en souvenez plus.',
      },
    ],
  },
  {
    title: 'Abonnements & paiement',
    items: [
      {
        q: 'Quelles sont les différences entre Free, Pro et Expert ?',
        a: 'Free : nombre limité d\'analyses IA par mois pour découvrir. Pro (19€/mois) : quota étendu + Scan Auto sur 3 assets. Expert (39€/mois) : analyses IA illimitées + Scan Auto jusqu\'à 10 assets + alertes prioritaires + fonctionnalités avancées de DCA et de sortie partielle.',
      },
      {
        q: 'Puis-je économiser en payant à l\'année ?',
        a: 'Oui. Le pack 3 mois est proposé à -10 %, et le pack annuel à -20 % par rapport au tarif mensuel équivalent. Ces packs sont des paiements one-shot sans renouvellement automatique — vous décidez de renouveler ou non à l\'échéance.',
      },
      {
        q: 'Comment annuler mon abonnement ?',
        a: 'Depuis "Mes paramètres" → "Abonnement" → "Résilier". L\'annulation prend effet à la fin de votre période payée en cours. Aucun frais de résiliation, aucune pénalité.',
      },
      {
        q: 'Puis-je obtenir un remboursement ?',
        a: 'Oui, dans les 7 jours suivant votre première souscription, à condition d\'avoir consommé moins de 5 analyses IA. Envoyez-nous un email via /contact et nous procédons au remboursement sous 48h ouvrées.',
      },
      {
        q: 'Que se passe-t-il si mon paiement échoue ?',
        a: 'Vous recevez immédiatement un email vous invitant à mettre à jour votre moyen de paiement. Votre abonnement passe en état "en attente" pendant 7 jours — pas de coupure immédiate. Vous conservez l\'accès en attendant la régularisation.',
      },
    ],
  },
  {
    title: 'Analyses IA & fonctionnalités',
    items: [
      {
        q: 'Comment fonctionnent les analyses IA ?',
        a: 'Vous uploadez un screenshot de graphique. L\'IA (basée sur GPT-4o Vision) analyse la structure de prix, identifie les niveaux clés (support/résistance, order blocks, FVG…) et vous propose : un point d\'entrée, un stop loss et 3 niveaux de take profit (TP1 25%, TP2 25%, TP100 50%). Le tout est aligné avec un profil de risque configurable.',
      },
      {
        q: 'Les analyses sont-elles fiables à 100 % ?',
        a: 'Aucune analyse en trading n\'est fiable à 100 %. TRADEX AI est un outil d\'aide à la décision, pas un système garantissant des gains. L\'IA excelle sur la détection de structures et la gestion du risque, mais ne peut anticiper les événements macro imprévus. Vous restez responsable de vos décisions.',
      },
      {
        q: 'Qu\'est-ce que le Scan Auto ?',
        a: 'Le Scan Auto surveille automatiquement une liste d\'actifs (BTC, ETH, actions US…) et vous notifie dès qu\'un setup de qualité est détecté. Vous définissez votre watchlist et la fréquence de scan, l\'IA fait le reste 24/7.',
      },
      {
        q: 'Qu\'est-ce que la fonction DCA ?',
        a: 'Le DCA (Dollar Cost Averaging) est un mode d\'entrée par tranches. Au lieu d\'ouvrir toute votre position d\'un coup, vous entrez progressivement sur des niveaux définis. Cela réduit votre PRU moyen si le prix bouge contre vous et améliore le RR global.',
      },
      {
        q: 'Puis-je exporter mes trades ?',
        a: 'Oui, depuis le journal, vous pouvez exporter tous vos trades en CSV (compatible Excel/Numbers). Utile pour votre déclaration fiscale ou vos analyses personnelles.',
      },
    ],
  },
  {
    title: 'Installer TRADEX AI comme une vraie app 📱',
    items: [
      {
        q: 'Pourquoi installer TRADEX AI comme une application ?',
        a: 'Une fois installée en PWA (Progressive Web App) sur votre écran d\'accueil, TRADEX AI se comporte comme une véritable application native : icône dédiée, lancement plein écran (sans barre d\'adresse), et surtout **réception des notifications push en temps réel même quand l\'app est fermée**. Vous ne loupez plus jamais un TP touché, un SL déclenché ou un setup Scan Auto détecté.',
      },
      {
        q: 'Comment installer TRADEX AI sur iPhone (iOS) ?',
        a: '1) Ouvrez tradex-ai.fr dans **Safari** (obligatoire, ça ne marche pas depuis Chrome/Firefox sur iOS).\n2) Appuyez sur l\'icône **Partager** (carré avec flèche vers le haut) en bas de l\'écran.\n3) Faites défiler et sélectionnez **"Sur l\'écran d\'accueil"**.\n4) Confirmez avec "Ajouter" en haut à droite.\n\nL\'icône TRADEX AI apparaît sur votre écran d\'accueil. Ouvrez-la comme une app normale. Au premier lancement, acceptez la demande d\'autorisation des notifications — c\'est essentiel pour recevoir les alertes en arrière-plan.',
      },
      {
        q: 'Comment installer TRADEX AI sur Android ?',
        a: '1) Ouvrez tradex-ai.fr dans **Chrome** (ou tout navigateur compatible PWA).\n2) Un bandeau **"Ajouter TRADEX AI à l\'écran d\'accueil"** apparaît automatiquement au bas de la page — appuyez sur **"Installer"**.\n3) Si le bandeau ne s\'affiche pas : menu ⋮ en haut à droite → **"Installer l\'application"** ou **"Ajouter à l\'écran d\'accueil"**.\n4) Confirmez.\n\nL\'app s\'installe dans votre tiroir d\'applications comme n\'importe quelle app. Acceptez les notifications au premier lancement pour recevoir les alertes push en arrière-plan.',
      },
      {
        q: 'Les notifications marchent-elles vraiment quand l\'app est fermée ?',
        a: 'Oui, à deux conditions strictes :\n\n1) L\'app doit être **installée via l\'écran d\'accueil** (pas juste ouverte dans un onglet du navigateur).\n2) Vous devez avoir **autorisé les notifications** au premier lancement (ou depuis les réglages système de votre téléphone : iOS Réglages → Notifications → TRADEX AI / Android Paramètres → Applications → TRADEX AI → Notifications).\n\nUne fois configuré, vous recevrez les alertes TP/SL touchés, setups Scan Auto détectés et changements de statut d\'abonnement, même téléphone verrouillé.',
      },
      {
        q: 'J\'ai refusé les notifications au premier lancement, comment les réactiver ?',
        a: '**iPhone** : Réglages iOS → Notifications → cherchez "TRADEX AI" → activez "Autoriser les notifications" + "Sons" + "Badges".\n\n**Android** : Paramètres → Applications → TRADEX AI → Notifications → activez toutes les catégories.\n\nEnsuite, ouvrez TRADEX AI → **Mes paramètres → Notifications** → activez le toggle "Notifications push" pour re-synchroniser.',
      },
      {
        q: 'L\'installation prend-elle de la place sur mon téléphone ?',
        a: 'Très peu : environ **10-15 Mo** (contre 80-150 Mo pour une app native équivalente). Les données de trades restent dans le cloud, donc l\'app locale reste légère. C\'est un des gros avantages de la technologie PWA.',
      },
      {
        q: 'Puis-je installer TRADEX AI sur mon ordinateur aussi ?',
        a: 'Oui, sur **Chrome desktop / Edge / Brave / Opera** : cliquez sur l\'icône **+** ou l\'ordinateur avec flèche dans la barre d\'adresse (à droite du champ URL), ou menu ⋮ → **"Installer TRADEX AI"**. L\'app apparaît dans votre Applications (Mac) ou menu Démarrer (Windows) et s\'ouvre dans sa propre fenêtre sans barre d\'adresse — pratique pour l\'utiliser côte-à-côte avec TradingView.',
      },
    ],
  },
  {
    title: 'Sécurité & données',
    items: [
      {
        q: 'Mes données sont-elles en sécurité ?',
        a: 'Oui. Toutes les communications sont chiffrées en TLS 1.3, les mots de passe hashés avec bcrypt, les bases de données stockées en Europe. Aucune revente de données à des tiers. Consultez notre /legal/rgpd pour plus de détails.',
      },
      {
        q: 'Puis-je supprimer mon compte définitivement ?',
        a: 'Oui, à tout moment. Rendez-vous dans "Mes paramètres" → "Supprimer mon compte" ou contactez-nous. Toutes vos données sont supprimées sous 30 jours (hors données de facturation conservées 10 ans pour raison légale).',
      },
      {
        q: 'Est-ce que TRADEX AI utilise mes graphiques pour entraîner son IA ?',
        a: 'Non. Nous utilisons OpenAI en mode API business avec "zero data retention" activé — vos images ne sont ni stockées, ni utilisées pour entraîner les modèles. Elles sont supprimées immédiatement après l\'analyse.',
      },
    ],
  },
  {
    title: 'Notifications & PWA',
    items: [
      {
        q: 'Comment activer les notifications push ?',
        a: 'À votre première connexion, TRADEX AI vous demande l\'autorisation. Si vous avez refusé, allez dans les paramètres de votre navigateur (ou iOS Settings → Notifications) et autorisez tradex-ai.fr. Vous recevez alors les alertes de TP/SL touchés et les setups détectés par Scan Auto.',
      },
      {
        q: 'La PWA fonctionne-t-elle hors ligne ?',
        a: 'Partiellement. L\'interface reste consultable, vous pouvez voir vos derniers trades cachés, mais les nouvelles analyses IA et les prix en temps réel nécessitent une connexion.',
      },
      {
        q: 'Comment mettre à jour la PWA après un nouveau déploiement ?',
        a: 'La PWA se met à jour automatiquement au prochain ouverture. Si vous voulez forcer une mise à jour immédiate, fermez complètement l\'app (swipe up sur iOS) et rouvrez-la. Depuis la version février 2026, l\'auto-update est encore plus rapide.',
      },
    ],
  },
  {
    title: 'Facturation',
    items: [
      {
        q: 'Puis-je changer de formule en cours d\'abonnement (upgrade / downgrade) ?',
        a: 'Oui. Depuis "Mes paramètres" → "Abonnement", vous pouvez passer de Pro à Expert (ou inversement) à tout moment. En cas d\'upgrade, la différence est facturée au prorata immédiat. En cas de downgrade, le nouveau tarif s\'applique au prochain cycle de facturation — vous conservez les fonctionnalités payées jusqu\'à la fin de la période en cours.',
      },
      {
        q: 'Est-ce que je reçois une facture pour ma comptabilité ?',
        a: 'Oui, une facture PDF conforme (avec TVA, SIRET et mentions légales) est générée automatiquement par Stripe à chaque paiement. Vous la recevez par email et pouvez la retrouver à tout moment depuis "Mes paramètres" → "Abonnement" → "Historique des paiements". Idéale pour votre comptabilité pro ou votre déclaration fiscale.',
      },
      {
        q: 'Les prix affichés sont-ils TTC ou HT ?',
        a: 'Tous les prix affichés sur le site (19€ Pro, 39€ Expert, packs 3 mois et annuels) sont **TTC** (Toutes Taxes Comprises). La TVA française (20 %) est incluse et clairement indiquée sur chaque facture.',
      },
    ],
  },
  {
    title: 'Contenu & Formation',
    items: [
      {
        q: 'Que contient l\'onglet Formation ?',
        a: 'L\'onglet Formation regroupe des cours structurés sur l\'analyse technique avancée : Smart Money Concepts (SMC), ICT (Inner Circle Trader), structure de marché, order blocks, FVG (Fair Value Gap), liquidité, et Money Management strict. Le contenu est conçu pour vous rendre autonome dans votre lecture des graphiques, en complément des analyses IA.',
      },
      {
        q: 'Y a-t-il des tutoriels vidéo ?',
        a: 'Les premiers cours vidéo sont en cours de production. En attendant, chaque module dispose d\'explications textuelles détaillées avec schémas et exemples réels de trades. Nous prévoyons d\'ajouter progressivement des tutoriels vidéo courts (3-5 min) sur les fonctionnalités clés de l\'app. Envie de suggérer un tuto en particulier ? Écrivez-nous via /contact.',
      },
    ],
  },
  {
    title: 'Support & Communauté',
    items: [
      {
        q: 'Y a-t-il un serveur Discord / Telegram TRADEX AI ?',
        a: 'Pas encore, mais c\'est prévu dans notre roadmap. Nous privilégions actuellement le support par email pour garantir une qualité de réponse premium. Si vous êtes intéressé pour rejoindre la communauté dès son ouverture, faites-le nous savoir via /contact — nous vous notifierons en priorité.',
      },
      {
        q: 'Puis-je proposer une fonctionnalité ?',
        a: 'Oui, et c\'est même vivement encouragé ! Écrivez-nous via /contact avec le sujet "Demande de fonctionnalité". Nous lisons chaque proposition, priorisons celles qui reviennent souvent, et implémentons régulièrement des features suggérées par la communauté. Plusieurs fonctionnalités actuelles (Scan Auto, DCA avancé, sorties partielles fixes) viennent directement de retours utilisateurs.',
      },
    ],
  },
];

export default function FAQ() {
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  const searchLower = search.trim().toLowerCase();
  const filteredSections = FAQ_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item =>
      !searchLower || item.q.toLowerCase().includes(searchLower) || item.a.toLowerCase().includes(searchLower)
    ),
  })).filter(section => section.items.length > 0);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="faq-page">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
          <HelpCircle className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold">Questions fréquentes</h1>
        <p className="text-muted-foreground text-lg">Tout ce que vous devez savoir sur TRADEX AI</p>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          type="text"
          placeholder="Rechercher une question…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="faq-search-input"
        />
      </div>

      {filteredSections.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          Aucun résultat pour « {search} ». <a href="/contact" className="text-primary hover:underline">Posez-nous directement votre question</a>.
        </Card>
      ) : filteredSections.map(section => (
        <div key={section.title}>
          <h2 className="text-lg font-bold text-primary mb-2 mt-6">{section.title}</h2>
          <div className="space-y-2">
            {section.items.map(item => {
              const isOpen = openId === item.q;
              return (
                <Card key={item.q} className="overflow-hidden">
                  <button
                    className="w-full p-4 text-left flex items-center gap-3 hover:bg-white/5 transition-colors"
                    onClick={() => setOpenId(isOpen ? null : item.q)}
                    data-testid={`faq-toggle-${item.q.substring(0, 30).replace(/\s+/g, '-')}`}
                  >
                    <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    <span className="font-medium text-sm">{item.q}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pl-11 text-sm text-muted-foreground leading-relaxed border-t border-white/5 pt-3">
                      {item.a}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      <Card className="p-6 mt-8 border-emerald-500/20 bg-emerald-500/5 flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
        <MessageSquare className="w-8 h-8 text-emerald-400 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">Vous ne trouvez pas votre réponse ?</div>
          <div className="text-sm text-muted-foreground">Notre équipe répond personnellement sous 24h ouvrées.</div>
        </div>
        <a
          href="/contact"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          data-testid="faq-contact-cta"
        >
          Nous contacter
        </a>
      </Card>
    </div>
  );
}
